import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL } from "../../config/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Alert, AlertDescription } from "../../components/ui/alert";
import "../../styles/derma.css";

/**
 * Resit selepas bayaran, dan jalan kembali ke dalam aplikasi.
 *
 * ToyyibPay mengembalikan penyumbang ke sini selepas FPX selesai. Untuk pengguna
 * iOS yang datang daripada aplikasi, halaman ini ialah jambatan pulang.
 *
 *
 * STATUS DIAMBIL SEMULA, TIDAK DIPERCAYAI DARIPADA URL
 *
 * Parameter pulangan gerbang kelihatan dalam bar alamat dan boleh disunting
 * sesiapa. Memaparkan "Berjaya" kerana URL berkata begitu bermakna sesiapa boleh
 * menghasilkan resit yang meyakinkan. Kebenaran datang daripada pelayan, yang
 * membacanya daripada rekod bayaran.
 *
 *
 * DUA PAUTAN PULANG, BUKAN SATU
 *
 * Pautan universal (https://) ialah yang utama kerana ia berfungsi walaupun
 * aplikasi tidak dipasang — ia hanya kekal di web. Tetapi ia gagal SENYAP apabila
 * fail apple-app-site-association belum di-cache oleh peranti, dan penyumbang
 * yang baru sahaja membayar tidak boleh dibiarkan terkandas. Jadi skema tersuai
 * ditawarkan sebagai sandaran.
 */

const STATUS_BERJAYA = "1";
const STATUS_PENDING = "2";

function ringgit(n) {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : null;
}

const Resit = () => {
    const { orderId: orderIdParam } = useParams();
    const [searchParams] = useSearchParams();

    // ToyyibPay memulangkan order_id sebagai parameter pertanyaan, tetapi
    // aplikasi memaut ke sini dengan ia dalam laluan. Kedua-duanya diterima.
    const orderId = orderIdParam || searchParams.get("order_id") || searchParams.get("orderId");
    const statusUrl = searchParams.get("status_id") || searchParams.get("status");

    const [memuat, setMemuat] = useState(true);
    const [data, setData] = useState(null);
    const [ralat, setRalat] = useState(null);

    const asasWeb = useMemo(() => String(API_URL).replace(/\/api\/?$/, ""), []);

    useEffect(() => {
        let dibatalkan = false;

        const muat = async () => {
            if (!orderId) {
                setRalat("Nombor rujukan transaksi tidak diterima.");
                setMemuat(false);
                return;
            }
            try {
                const r = await fetch(
                    `${asasWeb}/web/getReceipt?transactionReferenceNo=${encodeURIComponent(orderId)}`
                );
                const json = await r.json();
                if (dibatalkan) return;

                if (json?.status === 200 && json?.data) setData(json.data);
                else setRalat(json?.message || "Resit tidak dijumpai.");
            } catch (e) {
                if (!dibatalkan) setRalat("Tidak dapat menghubungi pelayan.");
            } finally {
                if (!dibatalkan) setMemuat(false);
            }
        };

        muat();
        return () => {
            dibatalkan = true;
        };
    }, [asasWeb, orderId]);

    const statusAkhir = String(data?.status_id ?? data?.billpayment_status ?? statusUrl ?? "");
    const berjaya = statusAkhir === STATUS_BERJAYA;
    const pending = statusAkhir === STATUS_PENDING;

    const pautanApp = orderId
        ? `aljariyah://derma/resit/${encodeURIComponent(orderId)}?status=${encodeURIComponent(statusAkhir || "")}`
        : null;

    if (memuat) {
        return (
            <div className="derma-root">
                <div className="flex min-h-screen items-center justify-center px-6">
                    <p className="text-sm text-muted-foreground">Memuatkan resit…</p>
                </div>
            </div>
        );
    }

    const tajuk = ralat
        ? "Resit tidak dapat dipaparkan"
        : berjaya
        ? "Sumbangan Berjaya"
        : pending
        ? "Sumbangan Dalam Proses"
        : "Sumbangan Tidak Berjaya";

    const mesej = ralat
        ? ralat
        : berjaya
        ? "Terima kasih. Semoga Allah membalas kebaikan anda."
        : pending
        ? "Bayaran anda sedang diproses. Resit akan dikemas kini sebaik sahaja disahkan."
        : "Bayaran anda tidak dapat diselesaikan. Tiada jumlah telah ditolak.";

    const warna = ralat || (!berjaya && !pending) ? "text-destructive" : berjaya ? "text-primary" : "text-amber-600";
    const sempadan = ralat || (!berjaya && !pending) ? "border-destructive" : berjaya ? "border-primary" : "border-amber-500";

    return (
        <div className="derma-root">
            <div className="mx-auto max-w-md px-5 pb-16 pt-12">
                <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 ${sempadan}`}>
                    <span className={`text-2xl ${warna}`}>{berjaya ? "✓" : pending ? "…" : "!"}</span>
                </div>

                <h1 className="mb-2 text-center text-xl font-semibold">{tajuk}</h1>
                <p className="mb-8 text-center text-sm leading-relaxed text-muted-foreground">{mesej}</p>

                {data ? (
                    <Card className="mb-8">
                        <CardContent className="p-5">
                            <Baris label="No. Rujukan" nilai={orderId} />
                            <Separator className="my-3" />
                            <Baris label="Institusi" nilai={data.organizationName} />
                            <Baris label="Tabung" nilai={data.tabungName} />
                            <Baris label="Penyumbang" nilai={data.payorName} />
                            <Baris label="Tarikh" nilai={data.paymentDate ?? data.createdDate} />
                            {ringgit(data.amount) ? (
                                <>
                                    <Separator className="my-3" />
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Jumlah</span>
                                        <span className="text-lg font-semibold">RM {ringgit(data.amount)}</span>
                                    </div>
                                </>
                            ) : null}
                        </CardContent>
                    </Card>
                ) : null}

                {!data && !ralat ? (
                    <Alert className="mb-8" variant="info">
                        <AlertDescription>
                            Butiran resit belum tersedia. Ia akan dihantar ke e-mel anda sebaik sahaja bayaran disahkan.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {pautanApp ? (
                    <div className="space-y-3">
                        <Button size="lg" className="w-full" asChild>
                            <a href={pautanApp}>Kembali ke Aplikasi</a>
                        </Button>
                        <p className="text-center text-xs text-muted-foreground">
                            Jika aplikasi tidak terbuka, anda boleh menutup tetingkap ini dengan selamat —
                            sumbangan anda telah direkodkan.
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

// Baris kosong disembunyikan sepenuhnya. Resit yang penuh dengan sengkang
// kelihatan seperti rekod yang rosak kepada penyumbang.
const Baris = ({ label, nilai }) => {
    if (nilai === null || nilai === undefined || nilai === "") return null;
    return (
        <div className="flex items-start justify-between gap-6 py-1.5">
            <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
            <span className="text-right text-sm">{String(nilai)}</span>
        </div>
    );
};

export default Resit;
